import { memo, useEffect, useState } from "react";
import { AnalyticsApiService } from "@zenith/services/transactions/services/AnalyticsApiService";
import type { AnalyticsDTO } from "@zenith/services/transactions/models/AnalyticsDTO";
import { OutletSummaryApiService } from "@zenith/services/transactions/services/OutletSummaryApiService";
import type { OutletTransactionSummaryDTO } from "@zenith/services/transactions/models/OutletTransactionSummaryDTO";
import type { PaymentChargedDTO } from "@zenith/services/transactions/models/PaymentChargedDTO";
import type { WeeklyDTO } from "@zenith/services/transactions/models/WeeklyDTO";
import { useOutletContext } from "@zenith/context";
import { useOutletView } from "@zenith/features/OutletManagement/hooks/useOutletView";
import { SpinnerComponent, SelectComponent } from "@zenith/components";
import { DEFAULT_CURRENCY_SYMBOL } from "@zenith/data/constants";
import { formatCurrency } from "@zenith/utils";
import { BarChartComponent } from "@zenith/features/Analytics";
import { EmptyState } from "../../EmptyStateComponent/empty-state-component/empty-state-component";
import { NoData3 } from "@zenith/assets/illustrations/NoData3";
import ServerErrorHandler from "@zenith/features/AppStateHandlers/ServerErrorHandler/server-error-handler";

// Helper function to format payment method labels
const formatPaymentMethodLabel = (paymentMethod: string): string => {
  const labelMap: Record<string, string> = {
    IN_BANK_TRANSFER: "Pay with Transfer",
    OUT_BANK_TRANSFER: "Funds Transfer",
    PAYMENT_LINK: "Payment Link",
    QR_CODE: "QR Code",
    CASH: "Cash",
    POS: "POS",
    USSD: "USSD",
  };
  return labelMap[paymentMethod] || paymentMethod;
};

type Timeframe = "weekly" | "monthly";

const SUMMARY_PERIOD: Record<Timeframe, "CURRENT_WEEK" | "CURRENT_MONTH"> = {
  weekly: "CURRENT_WEEK",
  monthly: "CURRENT_MONTH",
};

/** Every outlet's summary in the weekly/monthly shape the card renders. */
const toTimeframeData = (summary?: OutletTransactionSummaryDTO): WeeklyDTO => ({
  totalCharged: { amount: summary?.totalVolume ?? 0 },
  paymentCharged: (summary?.breakdown ?? []).map((item) => ({
    paymentMethod: item.paymentMethod as PaymentChargedDTO.paymentMethod,
    amount: item.transactionVolume ?? 0,
  })),
});

const PaymentSummaryComponent = memo(function PaymentSummaryComponent() {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsDTO | null>(null);
  const [allOutletsData, setAllOutletsData] = useState<WeeklyDTO>();
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<Timeframe>("weekly");
  const [serverError, setServerError] = useState(false);
  const { isAllOutletsSelected } = useOutletView();
  const { outletContext } = useOutletContext();
  const selectionKey = JSON.stringify(outletContext.selection);
  const summaryPeriod = isAllOutletsSelected
    ? SUMMARY_PERIOD[timeframe]
    : undefined;

  // Analytics belong to the signed-in outlet, so All Outlets totals every
  // outlet from the outlet summary instead.
  useEffect(() => {
    setLoading(true);
    setServerError(false);
    const request = summaryPeriod
      ? OutletSummaryApiService.getOutletSummaryFiltered(
          undefined,
          summaryPeriod
        ).then((response) => setAllOutletsData(toTimeframeData(response.data)))
      : AnalyticsApiService.totalCharged().then((response) => {
          if ("data" in response && response.data) {
            setAnalyticsData(response.data);
          } else {
            console.warn("No data in analytics response");
          }
        });

    request
      .catch((error) => {
        console.error("Error fetching analytics:", error);
        setServerError(true);
      })
      .finally(() => setLoading(false));
  }, [summaryPeriod, selectionKey]);

  const currentData = isAllOutletsSelected
    ? allOutletsData
    : timeframe === "weekly"
    ? analyticsData?.weekly
    : analyticsData?.monthly;
  const hasData = !!(
    currentData?.totalCharged?.amount || currentData?.totalCharged?.amount === 0
  );
  const hasNonZeroData = currentData?.paymentCharged?.some(
    (item) => (item.amount || 0) > 0
  );

  // Prepare chart data with formatted labels
  const chartData =
    currentData?.paymentCharged
      ?.filter((item) => (item.amount || 0) > 0)
      .map((item) => ({
        paymentMethod: formatPaymentMethodLabel(item.paymentMethod || ""),
        amount: item.amount || 0,
      })) || [];

  if (loading) {
    return (
      <div className="my-auto flex h-[calc(100vh-400px)] items-center justify-center ">
        <SpinnerComponent type="primary" size={40} />
      </div>
    );
  }

  if (serverError) {
    return (
      <div className="my-auto flex w-full flex-col items-center justify-center">
        <ServerErrorHandler
          errorTitle="We are unable to fetch analytics"
          errorMessage="Please try again. Apologies for the inconvenience."
        />
      </div>
    );
  }

  if (!hasData || !hasNonZeroData) {
    return (
      <div className="my-auto flex w-full flex-col items-center justify-center">
        <EmptyState
          title=" No Data Available"
          description="There is currently no data to display for this week."
          illustration={
            <div className="flex w-full justify-center">
              <NoData3 className="block h-auto w-40 text-primary-300" />
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className="h-auto px-2">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <p className="text-sm text-neutral-500">Total Charged</p>
          <p className="mt-1 flex items-center gap-1">
            <span className="text-xl text-neutral-500">
              {DEFAULT_CURRENCY_SYMBOL}{" "}
            </span>
            <span className="text-lg font-medium text-neutral-900 md:text-2xl">
              {currentData?.totalCharged &&
                formatCurrency(currentData.totalCharged.amount, false)}
            </span>
          </p>
        </div>
        <div className="w-32">
          <SelectComponent
            items={[
              { value: "weekly", label: "Weekly" },
              { value: "monthly", label: "Monthly" },
            ]}
            className="h-10 w-32 border-neutral-200"
            label=""
            id="time-frame-select"
            labelId="time-frame-select-label"
            defaultValue={timeframe}
            value={timeframe}
            onChange={(_event, value) =>
              value && setTimeframe(value as Timeframe)
            }
            showSelectedValueCheckIcon={true}
            showDivider={true}
          />
        </div>
      </div>
      <div className="h-auto w-full md:h-[240px]">
        {chartData.length > 0 && (
          <BarChartComponent
            data={chartData}
            indexBy="paymentMethod"
            keys={["amount"]}
            layout="vertical"
            margin={{ top: 40, right: 20, bottom: 60, left: 70 }}
            minWidth="100%"
            maxWidth="100%"
            height={240}
            border={false}
            width={250}
          />
        )}
      </div>
    </div>
  );
});

export default PaymentSummaryComponent;
